import { useState, type ReactNode } from 'react';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { ErrorNote, Field, Modal, Select, TextArea, errorMessage } from '@/components/ui';

export interface ActionField {
  key: string;
  label: string;
  type?: 'text' | 'date' | 'number' | 'month' | 'select' | 'textarea';
  options?: Array<{ value: string; label: string }>;
  required?: boolean;
  hint?: ReactNode;
  placeholder?: string;
}

/**
 * A small dialog that asks for what one action needs — a comment when
 * approving, the date and method when paying out, a reason when holding pay —
 * and runs it. Stays open with the server's message if the action is refused,
 * so nothing typed is lost.
 */
export function ActionDialog({
  title,
  icon,
  subject,
  intro,
  fields,
  initial,
  submitLabel,
  danger,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  icon?: IconKind;
  /** Who or what the action is on, as a `DialogSubject` card. */
  subject?: ReactNode;
  intro?: ReactNode;
  fields: ActionField[];
  initial?: Record<string, string>;
  submitLabel: string;
  danger?: boolean;
  onSubmit: (values: Record<string, string>) => Promise<unknown>;
  onClose: () => void;
  children?: ReactNode;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const f of fields) v[f.key] = initial?.[f.key] ?? '';
    return v;
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit() {
    const missing = fields.find((f) => f.required && !values[f.key]?.trim());
    if (missing) {
      setProblem(`${missing.label} is needed.`);
      return;
    }
    setProblem(null);
    setPending(true);
    try {
      await onSubmit(values);
      onClose();
    } catch (e) {
      setProblem(errorMessage(e, 'That did not work'));
    } finally {
      setPending(false);
    }
  }

  const set = (key: string) => (value: string) => setValues((v) => ({ ...v, [key]: value }));

  return (
    <Modal title={title} onClose={onClose} medium={fields.length > 2}>
      <h2 className={icon ? 'withicon' : undefined}>
        {icon && <AppIcon kind={icon} />}
        {title}
      </h2>
      {subject}
      {intro && <p className="muted">{intro}</p>}
      {children}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className={`formgrid formgrid--${fields.length > 2 ? 2 : 1}`}>
          {fields.map((f) =>
            f.type === 'select' ? (
              <Select
                key={f.key}
                label={f.label}
                value={values[f.key] ?? ''}
                onChange={set(f.key)}
                options={f.options ?? []}
                required={f.required ?? false}
                hint={f.hint}
              />
            ) : f.type === 'textarea' ? (
              <TextArea
                key={f.key}
                label={f.label}
                value={values[f.key] ?? ''}
                onChange={set(f.key)}
                required={f.required ?? false}
                hint={f.hint}
                placeholder={f.placeholder ?? ''}
                rows={2}
              />
            ) : (
              <Field
                key={f.key}
                label={f.label}
                type={f.type ?? 'text'}
                value={values[f.key] ?? ''}
                onChange={set(f.key)}
                required={f.required ?? false}
                hint={f.hint}
                placeholder={f.placeholder ?? ''}
              />
            ),
          )}
        </div>
        {problem && <ErrorNote message={problem} />}
        <div className="formactions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <span className="formactions__spacer" />
          <button type="submit" className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`} disabled={pending}>
            {pending ? 'Working…' : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
